<template>
  <div
    class="modal fade"
    id="dateRangeExportModal"
    tabindex="-1"
    aria-labelledby="dateRangeExportModalLabel"
    aria-hidden="true"
  >
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content date-range-export-modal">
        <div class="modal-header">
          <h5 class="modal-title" id="dateRangeExportModalLabel">{{ $t("ui.exportByDate") }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" :aria-label="$t('ui.cancel')"></button>
        </div>
        <div class="modal-body">
          <div class="date-range-presets" role="group" :aria-label="$t('ui.exportQuickRange')">
            <button
              type="button"
              class="date-range-preset"
              :class="{ active: preset === 'week' }"
              @click="applyPreset('week')"
            >
              {{ $t("ui.exportThisWeek") }}
            </button>
            <button
              type="button"
              class="date-range-preset"
              :class="{ active: preset === 'month' }"
              @click="applyPreset('month')"
            >
              {{ $t("ui.exportThisMonth") }}
            </button>
            <button
              type="button"
              class="date-range-preset"
              :class="{ active: preset === 'year' }"
              @click="applyPreset('year')"
            >
              {{ $t("ui.exportThisYear") }}
            </button>
          </div>

          <label class="date-range-field">
            <span>{{ $t("ui.exportStartDate") }}</span>
            <input type="date" v-model="startInput" @change="onManualRangeChange" />
          </label>
          <label class="date-range-field">
            <span>{{ $t("ui.exportEndDate") }}</span>
            <input type="date" v-model="endInput" @change="onManualRangeChange" />
          </label>

          <p v-if="error" class="date-range-error">{{ error }}</p>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-outline-secondary" data-bs-dismiss="modal">
            {{ $t("ui.cancel") }}
          </button>
          <button type="button" class="btn btn-primary" :disabled="exporting" @click="runExport">
            {{ exporting ? $t("settings.exporting") : $t("ui.exportByDate") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from "bootstrap";
import moment from "moment";
const {
  presetRange,
  normalizeDayId,
  exportDateRangeToFile,
} = require("../helpers/dateRangeExport.js");
const { fetchTodoListsRange, getDocument } = require("../sync/engine.js");

function toInputValue(dayId) {
  if (!dayId) return "";
  return moment(dayId, "YYYYMMDD").format("YYYY-MM-DD");
}

function fromInputValue(value) {
  return normalizeDayId(value);
}

export default {
  name: "DateRangeExportModal",
  data() {
    const initial = presetRange("week", new Date(), true);
    return {
      preset: "week",
      startInput: toInputValue(initial.start),
      endInput: toInputValue(initial.end),
      exporting: false,
      error: "",
    };
  },
  computed: {
    weekStartsOnMonday() {
      return !!this.$store.getters.config.weekStartOnMonday;
    },
    customListIds() {
      return this.$store.getters.cTodoListIds || [];
    },
  },
  mounted() {
    const el = document.getElementById("dateRangeExportModal");
    if (el) {
      el.addEventListener("show.bs.modal", this.resetToWeekPreset);
    }
  },
  beforeUnmount() {
    const el = document.getElementById("dateRangeExportModal");
    if (el) {
      el.removeEventListener("show.bs.modal", this.resetToWeekPreset);
    }
  },
  methods: {
    resetToWeekPreset() {
      this.applyPreset("week");
      this.error = "";
      this.exporting = false;
    },
    applyPreset(kind) {
      this.preset = kind;
      const range = presetRange(kind, new Date(), this.weekStartsOnMonday);
      this.startInput = toInputValue(range.start);
      this.endInput = toInputValue(range.end);
      this.error = "";
    },
    onManualRangeChange() {
      this.preset = "custom";
      let start = fromInputValue(this.startInput);
      let end = fromInputValue(this.endInput);
      if (start && end && start > end) {
        const tmp = start;
        start = end;
        end = tmp;
        this.startInput = toInputValue(start);
        this.endInput = toInputValue(end);
      }
    },
    hideModalById(id) {
      const el = document.getElementById(id);
      if (!el) return;
      const instance = Modal.getInstance(el) || Modal.getOrCreateInstance(el);
      instance.hide();
      // Nested/stacked Bootstrap modals can leave a stuck backdrop.
      el.classList.remove("show");
      el.style.display = "none";
      el.setAttribute("aria-hidden", "true");
      el.removeAttribute("aria-modal");
    },
    clearModalArtifacts() {
      document.querySelectorAll(".modal-backdrop").forEach((node) => node.remove());
      document.body.classList.remove("modal-open");
      document.body.style.removeProperty("overflow");
      document.body.style.removeProperty("padding-right");
    },
    async runExport() {
      this.error = "";
      const start = fromInputValue(this.startInput);
      const end = fromInputValue(this.endInput);
      if (!start || !end) {
        this.error = this.$t("ui.exportInvalidRange");
        return;
      }
      this.exporting = true;
      try {
        await exportDateRangeToFile({
          start,
          end,
          customListIds: this.customListIds,
          weekStartsOnMonday: this.weekStartsOnMonday,
          fetchRange: (from, to) => fetchTodoListsRange(from, to),
          getDocument,
        });
        this.hideModalById("dateRangeExportModal");
        this.hideModalById("exportingModal");
        this.clearModalArtifacts();
      } catch (e) {
        this.error = e && e.message ? e.message : this.$t("ui.exportFailed");
        this.hideModalById("exportingModal");
        this.clearModalArtifacts();
      } finally {
        this.exporting = false;
      }
    },
  },
};
</script>

<style scoped>
.date-range-export-modal {
  border: none;
  border-radius: 14px;
  background: var(--m-page, #f2f2f7);
  overflow: hidden;
}

.modal-header,
.modal-footer {
  border: none;
  background: transparent;
}

.modal-title {
  font-size: 17px;
  font-weight: 650;
}

.modal-body {
  padding-top: 0;
}

.date-range-presets {
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
}

.date-range-preset {
  flex: 1;
  border: none;
  border-radius: 10px;
  min-height: 36px;
  padding: 8px 6px;
  font-size: 13px;
  font-weight: 600;
  color: inherit;
  background: rgba(120, 120, 128, 0.12);
  cursor: pointer;
}

.date-range-preset.active {
  background: rgba(13, 110, 253, 0.14);
  color: #0d6efd;
}

.dark-theme .date-range-preset.active {
  background: rgba(10, 132, 255, 0.28);
  color: #0a84ff;
}

.date-range-field {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 12px;
  font-size: 13px;
  color: var(--m-muted, rgba(60, 60, 67, 0.6));
}

.date-range-field input {
  border: 1px solid var(--m-hairline, rgba(60, 60, 67, 0.18));
  border-radius: 10px;
  min-height: 42px;
  padding: 8px 12px;
  font-size: 16px;
  color: inherit;
  background: var(--m-surface, #fff);
}

.date-range-error {
  margin: 0;
  color: #c62828;
  font-size: 13px;
}
</style>
